import {getUser} from '@gaia/stores/user';
import {fetchJSONMemoized} from '@gaia/utils/fetchJSON';
import derefMapboxStyle from '@gaia/utils/mapbox/derefMapboxStyle';
import opacifyMapboxStyle from '@gaia/utils/mapbox/opacifyMapboxStyle';
import resolveMapboxURL from '@gaia/utils/mapbox/resolveMapboxURL';
import mapValues from 'lodash/mapValues';
import memoize from 'lodash/memoize';
import type {AnyLayer, Style} from 'mapbox-gl';

import resolveGaiaURL from './resolveGaiaURL';

const deprecatedTileUrl = 'https://statictiles.gaiagps.com/not-available.png';

interface FetchLayerMapboxStyleOptions {
    preview?: boolean;
    id?: string;
}

async function fetchLayerMapboxStyle(
    layerTypes: {[key: string]: LayerType},
    layer: Layer,
    options: FetchLayerMapboxStyleOptions
) {
    return opacifyMapboxStyle(
        layer.opacity,
        await fetchLayerTypeMapboxStyle(layerTypes, layer, options)
    );
}

export default memoize(fetchLayerMapboxStyle, (layerTypes, layer, options) =>
    JSON.stringify([layerTypes[layer.type], layer, options])
);

export async function fetchLayerTypeMapboxStyle(
    layerTypes: {[key: string]: LayerType},
    layer: Layer,
    {preview = false, id}: FetchLayerMapboxStyleOptions = {}
): Promise<Style> {
    const layerType = layerTypes[layer.type];
    const previewStyle = preview && layerType.tilesPreview && layerType.tilesPreview.length;

    let layerMapboxStyle: Partial<Style>;

    if (!layerType.styleURL || previewStyle) {
        const {corsProxyUrl} = await getUser();
        layerMapboxStyle = fetchRasterLayerTypeMapboxStyle(layerType, {preview}, corsProxyUrl);
    } else {
        layerMapboxStyle = await fetchVectorLayerTypeMapboxStyle(layerType, layerTypes);
    }

    return {
        sources: {},
        layers: [],
        ...layerMapboxStyle,
        metadata: {...layerType, ...(id && {id})},
        name: layerType.name,
        version: 8,
        zoom: layerType.center ? layerType.center[2] : 1,
    };
}

async function fetchVectorLayerTypeMapboxStyle(
    type: LayerType,
    types: {[key: string]: LayerType}
): Promise<Style> {
    if (!type.styleURL) throw new Error();

    const url = resolveMapboxURL(type.styleURL);
    const style = derefMapboxStyle(await fetchJSONMemoized(url));

    return {
        ...style,
        layers: (style.layers || []).filter(
            (layer) => type.basemap || layer.type !== 'background'
        ) as AnyLayer[],
        sources: mapValues(style.sources || {}, (source: any) => {
            if (!source.tiles) return source;
            const tiles = source.tiles
                .map((tileURL: string) => resolveGaiaURL(types, tileURL))
                .flat();

            return {
                ...source,
                tiles,
            };
        }),
    };
}

function fetchRasterLayerTypeMapboxStyle(
    type: LayerType,
    {preview}: FetchLayerMapboxStyleOptions,
    corsProxyUrl: string
) {
    let tiles: string[];
    let isHD = false;
    if (preview && type.tilesPreview && type.tilesPreview.length) {
        tiles = type.tilesPreview;
    } else if (type.tilesHD && type.tilesHD.length && window.devicePixelRatio > 1) {
        tiles = type.tilesHD;
        isHD = true;
    } else {
        tiles = type.tiles;
    }

    return {
        sources: {
            source: {
                type: 'raster',
                attribution: type.attribution,
                bounds: type.bounds,
                scheme: type.scheme,
                tiles: tiles.map((url) =>
                    type.deprecated
                        ? deprecatedTileUrl
                        : applyCORSProxyURL(url, corsProxyUrl, {...type})
                ),
                minzoom: type.minzoom,
                maxzoom: type.maxzoom,
                ...(isHD && type.tileSizeHD && {tileSize: type.tileSizeHD / 2}),
                ...(!isHD && type.tileSize && {tileSize: type.tileSize}),
            },
        },
        layers: [
            {
                id: 'layer',
                type: 'raster',
                source: 'source',
            },
        ],
    } as Partial<Style>;
}

export function applyCORSProxyURL(
    url: string,
    corsProxyUrl: string,
    {supportsCORS, displayKey}: LayerType
) {
    // Custom layers use third-party tile URLs and should always
    // go through the proxy to ensure they display as expected
    // We used to save custom layers with supportsCORS = true, so we have to check
    // for the absence of a displayKey in order to reasonably identify a custom layer.
    if (supportsCORS && displayKey) return url;

    return `${corsProxyUrl}${encodeURIComponent(url)}`
        .replace(encodeURIComponent('{z}'), '{z}')
        .replace(encodeURIComponent('{x}'), '{x}')
        .replace(encodeURIComponent('{y}'), '{y}');
}
